import React, { useState, useEffect } from "react";

const BalanceTransferCalculator = () => {
  const [outstandingLoan, setOutstandingLoan] = useState(1500000);
  const [currentInterestRate, setCurrentInterestRate] = useState(9.5);
  const [remainingTenure, setRemainingTenure] = useState(15);
  const [tenureType, setTenureType] = useState("years");
  const [newInterestRate, setNewInterestRate] = useState(8.0);
  const [processingFee, setProcessingFee] = useState(15000);
  const [result, setResult] = useState({
    currentEMI: "0.00",
    newEMI: "0.00",
    currentTotalPayment: "0.00",
    newTotalPayment: "0.00",
    currentInterest: "0.00",
    newInterest: "0.00",
    totalSavings: "0.00",
    netSavings: "0.00",
  });

  // Balance Transfer Calculation Function
  const calculateBalanceTransfer = () => {
    const P = parseFloat(outstandingLoan);
    const currentRate = parseFloat(currentInterestRate) / 100;
    const newRate = parseFloat(newInterestRate) / 100;
    const r_current = currentRate / 12;
    const r_new = newRate / 12;
    let tenure = parseFloat(remainingTenure);
    const fee = parseFloat(processingFee);

    if (isNaN(P) || isNaN(r_current) || isNaN(r_new) || isNaN(tenure) || P <= 0 || tenure <= 0) return;

    const n = tenureType === "years" ? tenure * 12 : tenure;

    // Calculate Current EMI
    let currentEMI;
    if (r_current > 0) {
      currentEMI = (P * r_current * Math.pow(1 + r_current, n)) / (Math.pow(1 + r_current, n) - 1);
    } else {
      currentEMI = P / n;
    }

    // Calculate New EMI
    let newEMI;
    if (r_new > 0) {
      newEMI = (P * r_new * Math.pow(1 + r_new, n)) / (Math.pow(1 + r_new, n) - 1);
    } else {
      newEMI = P / n;
    }

    // Calculate total payments and interest
    const currentTotalPayment = currentEMI * n;
    const newTotalPayment = newEMI * n;
    const currentInterest = currentTotalPayment - P;
    const newInterest = newTotalPayment - P;
    
    const totalSavings = currentInterest - newInterest;
    const netSavings = totalSavings - fee;

    setResult({
      currentEMI: currentEMI.toFixed(2),
      newEMI: newEMI.toFixed(2),
      currentTotalPayment: currentTotalPayment.toFixed(2),
      newTotalPayment: newTotalPayment.toFixed(2),
      currentInterest: currentInterest.toFixed(2),
      newInterest: newInterest.toFixed(2),
      totalSavings: totalSavings.toFixed(2),
      netSavings: netSavings.toFixed(2),
    });
  };

  useEffect(() => {
  calculateBalanceTransfer();
}, [outstandingLoan, currentInterestRate, remainingTenure, tenureType, newInterestRate, processingFee]);

  const tenureLabel = tenureType === "years" ? "Years" : "Months";

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path>
              <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path>
              <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path>
            </svg>
          </div>
          <h1>Loan Balance Transfer Calculator</h1>
          <p>
            Compare your current loan with a balance transfer offer to see if switching lenders saves you money
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Loan Details
              </h2>

              {/* Outstanding Loan Amount */}
              <div className="input-group">
                <label>Outstanding Loan Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={outstandingLoan}
                    onChange={(e) => setOutstandingLoan(e.target.value)}
                    className="calc-input"
                    placeholder="1500000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 1500000, 2500000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOutstandingLoan(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 100000).toFixed(0)}L
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Interest Rate */}
              <div className="input-group">
                <label>Current Interest Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={currentInterestRate}
                    onChange={(e) => setCurrentInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="9.5"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[8.5, 9.5, 10.5, 11.5].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setCurrentInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Remaining Tenure */}
              <div className="input-group">
                <label>Remaining Loan Tenure</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={remainingTenure}
                    onChange={(e) => setRemainingTenure(e.target.value)}
                    className="calc-input"
                    placeholder="15"
                  />
                </div>
                <div className="quick-select-group">
                  {tenureType === "years" 
                    ? [5, 10, 15, 20, 25].map((years) => (
                        <button
                          key={years}
                          onClick={() => setRemainingTenure(years)}
                          className="quick-select-btn success"
                        >
                          {years}Y
                        </button>
                      ))
                    : [12, 24, 60, 120].map((months) => (
                        <button
                          key={months}
                          onClick={() => setRemainingTenure(months)}
                          className="quick-select-btn success"
                        >
                          {months}M
                        </button>
                      ))
                  }
                </div>
              </div>

              {/* Tenure Type */}
              <div className="input-group">
                <label>Tenure Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "years", label: "Years" },
                    { value: "months", label: "Months" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setTenureType(type.value);
                        setRemainingTenure(type.value === "years" ? 15 : 180);
                      }}
                      className={`quick-select-btn ${tenureType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* New Interest Rate */}
              <div className="input-group">
                <label>New Interest Rate (After Transfer)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={newInterestRate}
                    onChange={(e) => setNewInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="8.0"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[7.5, 8.0, 8.5, 9.0].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setNewInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Processing Fee */}
              <div className="input-group">
                <label>Processing Fee & Charges</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={processingFee}
                    onChange={(e) => setProcessingFee(e.target.value)}
                    className="calc-input"
                    placeholder="15000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 10000, 15000, 25000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setProcessingFee(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateBalanceTransfer} className="calculate-btn">
                Compare Loans
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Transfer Analysis
              </h2>

              <div className="result-card">
                {/* Net Savings - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Net Savings After Transfer</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.netSavings).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {parseFloat(result.netSavings) > 0 ? "Transfer is beneficial!" : "Transfer may not be worth it"}
                  </div>
                </div>

                {/* Current EMI */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Current EMI</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.currentEMI).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At {currentInterestRate}% interest
                  </div>
                </div>

                {/* New EMI */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">New EMI After Transfer</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.newEMI).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At {newInterestRate}% interest
                  </div>
                </div>

                {/* Monthly Savings */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Monthly EMI Savings</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{(parseFloat(result.currentEMI) - parseFloat(result.newEMI)).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Save every month
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Interest Comparison</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Current Interest</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.currentInterest).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.currentInterest) / parseFloat(result.currentInterest)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">New Interest</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.newInterest).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.newInterest) / parseFloat(result.currentInterest)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>What is Loan Balance Transfer?</h3>
              <p>
                Loan balance transfer is the process of moving your outstanding loan from one lender to another 
                that offers better interest rates or terms. When interest rates drop or your credit profile improves, 
                you may qualify for lower rates than your current loan. By transferring the balance, you can reduce 
                your EMI, save on total interest, or both. This calculator helps you evaluate whether a balance 
                transfer makes financial sense by comparing your current loan costs with the new offer, factoring 
                in processing fees and other charges. A balance transfer is typically beneficial when the interest 
                rate difference is at least 0.5-1% and you have substantial remaining tenure.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Balance Transfer Savings Formula:</h4>
            <div className="formula-display">
              Net Savings = (Current Interest - New Interest) - Processing Fees
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Current Interest</div>
                <div className="formula-param-value">Total interest on existing loan</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">New Interest</div>
                <div className="formula-param-value">Total interest after transfer</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Processing Fees</div>
                <div className="formula-param-value">Charges for balance transfer</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Net Savings</div>
                <div className="formula-param-value">Actual benefit after all costs</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>When should I consider a loan balance transfer?</h5>
              <p>
                Consider balance transfer when you find interest rates 0.5-1% lower than your current rate, have 
                significant remaining tenure (at least 5-10 years for home loans, 2-3 years for other loans), your 
                credit score has improved since taking the original loan, or when there's intense competition among 
                lenders offering attractive rates. Balance transfers are most beneficial early in your loan tenure 
                when the outstanding principal is high. Don't transfer if you're in the last few years of your loan, 
                as savings may not justify processing fees and documentation hassles. Also consider if your current 
                lender can match the new rate through loan restructuring.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the costs involved in a balance transfer?</h5>
              <p>
                Balance transfer costs include processing fees (0.5-2% of outstanding amount, typically ₹5,000-50,000), 
                legal and technical valuation charges for home loans (₹5,000-15,000), prepayment penalties on existing 
                loan if applicable (usually waived for floating rate home loans), stamp duty and registration charges 
                (varies by state), insurance costs if required, and administrative charges. Some banks offer zero or 
                minimal processing fees during promotional periods. Calculate total costs and ensure savings exceed 
                these expenses. Factor in time and effort for documentation. Some lenders may offer to absorb certain 
                costs to attract customers.
              </p>
            </div>

            <div className="faq-item">
              <h5>How long does the balance transfer process take?</h5>
              <p>
                Balance transfer typically takes 15-45 days depending on loan type and lender efficiency. The process 
                involves submitting application to new lender, document verification and credit appraisal (5-7 days), 
                property valuation for secured loans (7-10 days), loan sanction and documentation (5-7 days), and 
                disbursement to old lender and account closure (3-5 days). Home loan transfers take longer due to 
                property checks and legal verification. Personal and car loan transfers are faster. During this period, 
                continue paying EMIs to your current lender. The new lender handles payoff to old lender. Keep tracking 
                the process and maintain communication with both lenders.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can my current lender match the new interest rate offer?</h5>
              <p>
                Yes, many lenders prefer retaining customers rather than losing them. Before initiating balance transfer, 
                negotiate with your current lender. Show them competitive offers and request a rate reduction or loan 
                restructuring. Lenders often have retention teams who can offer better rates, especially for customers 
                with good repayment history. This saves you from processing fees and documentation hassles. However, 
                banks typically reduce rates by only 0.25-0.5% through negotiation. If the new offer is significantly 
                better and your lender won't match it, proceed with transfer. Sometimes lenders offer rate reductions 
                with conditions like tenure extension or additional products.
              </p>
            </div>

            <div className="faq-item">
              <h5>What documents are required for balance transfer?</h5>
              <p>
                Documents needed include loan sanction letter and statement from current lender showing outstanding 
                amount, last 6-12 months repayment track record, income proof (salary slips, ITR, Form 16), bank 
                statements for 6 months, identity and address proof, property documents for secured loans, existing 
                loan agreement copy, and latest property valuation report. Self-employed individuals need business 
                documents and financial statements. Good credit score (750+) is essential. New lender may request 
                additional documents during processing. Keep all originals and multiple copies ready. Some lenders 
                accept digital documentation for faster processing.
              </p>
            </div>

            <div className="faq-item">
              <h5>Are there any risks or downsides to balance transfer?</h5>
              <p>
                Potential downsides include processing fees eating into savings if not calculated properly, extended 
                loan tenure if you don't maintain same EMI (though new EMI is lower, you pay more total interest), 
                loss of existing lender benefits like pre-approved top-up loans or flexible prepayment options, hard 
                credit inquiry impacting score temporarily, new lender's hidden charges or stringent terms, and risk 
                of falling into a cycle of repeated transfers chasing marginal rate differences. Some new lenders have 
                stricter prepayment penalties or higher charges for subsequent transactions. Always read fine print, 
                compare total cost over remaining tenure, and ensure net savings justify the effort and potential risks.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceTransferCalculator;